import { createFileRoute } from "@tanstack/react-router";
import { useI18n } from "@/lib/i18n";
import { translatePortuguese } from "@/data/portuguese-core";
import { localizedCoreHead } from "@/lib/core-head";
import { useEffect, useState } from "react";
import { z } from "zod";

import { HomeNav } from "@/components/home-nav";
import { SiteFooter } from "@/components/site-footer";
import { VideoEmbed } from "@/components/video-embed";
import contactVideoPoster from "@/assets/contact-video-poster.jpg.asset.json";
import { sendContactMessage } from "@/lib/contact.functions";
import studio0 from "@/assets/studio.webp.asset.json";
import studio1 from "@/assets/studio_1.webp.asset.json";
import studio2 from "@/assets/studio_2.webp.asset.json";
import studio3 from "@/assets/studio_3.webp.asset.json";
import studio4 from "@/assets/studio_4.webp.asset.json";
import studio5 from "@/assets/studio_5.webp.asset.json";
import studio6 from "@/assets/studio_6.webp.asset.json";

export const Route = createFileRoute("/contact")({
  head: ({ matches }) => localizedCoreHead("/contact", matches[0]?.context.getLang() ?? "en", {
    meta: [
      { property: "og:url", content: "https://www.luizajesusdoprado.com/contact" },
      { title: "Contact — Luiza Jesus do Prado" },
      {
        name: "description",
        content:
          "Contact Luiza Jesus do Prado for exhibitions, performances, residencies, commissions, collaborations and gallery representation. Based worldwide.",
      },
      { property: "og:title", content: "Contact — Luiza Jesus do Prado" },
      {
        property: "og:description",
        content:
          "Exhibitions, performances, residencies, commissions and representation. Based worldwide.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: Contact,
});


const topics = [
  "Exhibition",
  "Performance",
  "Residency",
  "Commission",
  "Collaboration",
  "Press",
  "Other",
];

const studioImages = [studio0, studio1, studio2, studio3, studio4, studio5, studio6];

const formSchema = z.object({
  name: z.string().trim().min(1, "Please tell me who you are").max(120, "That name is too long"),
  email: z
    .string()
    .trim()
    .min(1, "Please add an email so I can reply")
    .max(200, "That email is too long")
    .email("Please check this email address"),
  subject: z.string().trim().max(160, "That subject is too long"),
  message: z
    .string()
    .trim()
    .min(10, "Please write a little more")
    .max(4000, "Please keep it under 4000 characters"),
});

type FieldErrors = Partial<Record<"name" | "email" | "subject" | "message", string>>;

function StudioCarousel() {
  const { lang } = useI18n();
  const tr = (text: string) => translatePortuguese(text, lang);
  const [i, setI] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setI((v) => (v + 1) % studioImages.length), 3600);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div className="relative h-[46vh] w-full overflow-hidden md:h-full md:min-h-[520px]">
      {studioImages.map((img, idx) => (
        <img
          key={img.url}
          src={img.url}
          alt={tr("Studio view")}
          loading="lazy"
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[1400ms] ${
            idx === i ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}
      <span className="absolute bottom-3 right-3 font-mono text-[9px] uppercase tracking-[0.2em] text-bone/60">
        {lang === "pt" ? tr("Studio") : studioImages[i % studioImages.length]?.url.split("/").pop()?.replace(/\.[^.]+$/, "")}
      </span>
    </div>
  );
}

const labelClass = "font-mono text-[10px] uppercase tracking-[0.18em] text-bone/65";
const fieldClass =
  "w-full border-b border-bone/20 bg-transparent py-3 font-sans text-[15px] font-light text-bone outline-none transition-colors placeholder:text-bone/65 focus:border-red";

function MessageForm() {
  const { lang } = useI18n();
  const tr = (text: string) => translatePortuguese(text, lang);
  const [values, setValues] = useState({ name: "", email: "", subject: "", message: "" });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [status, setStatus] = useState<"idle" | "sending" | "sent">("idle");
  const [notice, setNotice] = useState("");

  const set = (key: keyof typeof values) => (event: { target: { value: string } }) => {
    setValues((current) => ({ ...current, [key]: event.target.value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
    setNotice("");
  };

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    const parsed = formSchema.safeParse(values);
    if (!parsed.success) {
      const next: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (key === "name" || key === "email" || key === "subject" || key === "message") {
          next[key] = tr(issue.message);
        }
      }
      setErrors(next);
      return;
    }

    setStatus("sending");
    const result = await sendContactMessage({
      data: {
        name: parsed.data.name,
        email: parsed.data.email,
        subject: parsed.data.subject,
        message: parsed.data.message,
      },
    });

    if (!result.ok) {
      setStatus("idle");
      setNotice(tr(result.error));
      return;
    }

    setValues({ name: "", email: "", subject: "", message: "" });
    setStatus("sent");
  }

  if (status === "sent") {
    return (
      <div className="border border-bone/20 p-8 md:p-12">
        <p className="font-display text-[clamp(1.8rem,4vw,2.8rem)] font-light leading-[1.05]">{tr("Message received")}</p>
        <p className="mt-4 max-w-md font-sans text-[13px] font-light leading-[1.7] text-bone/70">{tr("Thank you for writing. Your message is saved and available for reply.")}</p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-8 font-mono text-[10px] uppercase tracking-[0.18em] text-bone/60 underline decoration-bone/30 underline-offset-4 hover:text-bone"
        >{tr("Send another message")}</button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="grid gap-8">
      <div className="grid gap-8 sm:grid-cols-2">
        <div>
          <label className={labelClass} htmlFor="contact-name">{tr("Name")}</label>
          <input
            id="contact-name"
            name="name"
            autoComplete="name"
            value={values.name}
            onChange={set("name")}
            className={fieldClass}
            placeholder={tr("Your name")}
          />
          {errors.name && (
            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.14em] text-red">
              {errors.name}
            </p>
          )}
        </div>
        <div>
          <label className={labelClass} htmlFor="contact-email">{tr("Email")}</label>
          <input
            id="contact-email"
            name="email"
            type="email"
            autoComplete="email"
            value={values.email}
            onChange={set("email")}
            className={fieldClass}
            placeholder={tr("you@studio.com")}
          />
          {errors.email && (
            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.14em] text-red">
              {errors.email}
            </p>
          )}
        </div>
      </div>

      <div>
        <label className={labelClass} htmlFor="contact-subject">{tr("Topic")}</label>
        <select
          id="contact-subject"
          name="subject"
          value={values.subject}
          onChange={set("subject")}
          className={`${fieldClass} appearance-none rounded-none`}
        >
          <option value="" className="bg-ink">{tr("Select a topic")}</option>
          {topics.map((topic) => (
            <option key={topic} value={topic} className="bg-ink">
              {tr(topic)}
            </option>
          ))}
        </select>
        {errors.subject && (
          <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.14em] text-red">
            {errors.subject}
          </p>
        )}
      </div>

      <div>
        <label className={labelClass} htmlFor="contact-message">{tr("Message")}</label>
        <textarea
          id="contact-message"
          name="message"
          rows={6}
          value={values.message}
          onChange={set("message")}
          className={`${fieldClass} resize-none leading-[1.7]`}
          placeholder={tr("Tell me about the project, place and dates.")}
        />
        {errors.message && (
          <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.14em] text-red">
            {errors.message}
          </p>
        )}
      </div>

      {/* Honeypot — hidden from people, caught by bots. */}
      <input
        tabIndex={-1}
        autoComplete="off"
        name="website"
        aria-hidden="true"
        className="sr-only"
        readOnly
        value=""
      />

      <div className="flex flex-col items-start gap-5 sm:flex-row sm:items-center sm:gap-10">
        <button
          type="submit"
          disabled={status === "sending"}
          className="border border-bone/40 px-8 py-4 font-mono text-[10px] uppercase tracking-[0.22em] text-bone transition-colors duration-300 hover:border-bone hover:bg-bone hover:text-ink disabled:cursor-wait disabled:opacity-50"
        >
          {status === "sending" ? tr("Sending") : tr("Send message")}
        </button>
        <p className="w-full font-mono text-[10px] uppercase leading-[1.6] tracking-[0.12em] text-bone/65 sm:max-w-[22rem]">
          {notice || tr("Messages reach the studio directly.")}
        </p>
      </div>
    </form>
  );
}

function Contact() {
  const { lang } = useI18n();
  const tr = (text: string) => translatePortuguese(text, lang);
  return (
    <div className="min-h-screen bg-ink text-bone">
      <HomeNav />

      <main id="main-content" className="mx-auto max-w-[1400px] px-6 md:px-10">
        <section className="grid grid-cols-12 gap-6 border-b border-bone/15 pt-32 pb-14 md:pt-44 md:pb-20">
          <div className="col-span-12 flex flex-col justify-between gap-10 md:col-span-6">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-bone/65">{tr("Based worldwide")}</p>
              <h1 className="mt-6 font-display text-[clamp(2.8rem,7vw,6rem)] font-light leading-[0.92] tracking-[0.01em]">{tr("Contact")}</h1>
              <p className="mt-8 max-w-md font-sans text-[14px] font-light leading-[1.75] text-bone/70">{tr("Available for exhibitions, residencies, commissions, performances and collaborations — and open to galleries and managements interested in a long-term dialogue.")}</p>
            </div>
          </div>
          <div className="col-span-12 md:col-span-6">
            <StudioCarousel />
          </div>
        </section>

        <section className="grid grid-cols-12 gap-x-6 gap-y-16 py-16 md:gap-x-10 md:py-24">
          <div className="col-span-12 md:col-span-7">
            <h2 className="font-mono text-[10px] uppercase tracking-[0.24em] text-bone/65">{tr("Write")}</h2>
            <p className="mt-4 max-w-lg font-display text-[clamp(1.9rem,4vw,3rem)] font-light leading-[1.05]">{tr("Send a message")}</p>
            <div className="mt-10">
              <MessageForm />
            </div>
          </div>

          <aside className="col-span-12 md:col-span-4 md:col-start-9">
            <div className="space-y-12">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-bone/65">{tr("Direct")}</p>
                <div className="mt-4 space-y-1 font-sans text-[13px] font-light leading-[1.7] text-bone/80">
                  <a
                    href="mailto:contact@luizajesusdoprado.com"
                    className="block w-fit hover:text-red"
                  >
                    contact@luizajesusdoprado.com
                  </a>
                  <a
                    href="mailto:luizajesusdoprado@gmail.com"
                    className="block w-fit hover:text-red"
                  >
                    luizajesusdoprado@gmail.com
                  </a>
                  <a href="tel:+5511914000809" className="block w-fit hover:text-red">
                    +55 11 91400-0809
                  </a>
                </div>
              </div>

              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-bone/65">{tr("Representation")}</p>
                <div className="mt-4 space-y-1 font-sans text-[13px] font-light leading-[1.7] text-bone/80">
                  <p>{tr("Currently looking for gallery representation")}</p>
                  <a
                    href="https://www.oculto.management/artists/luiza-jesus-do-prado"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-fit underline decoration-bone/30 underline-offset-4 hover:text-red"
                  >{tr("Oculto")}</a>
                </div>
              </div>

              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-bone/65">{tr("Studio")}</p>
                <div className="mt-4 space-y-1 font-sans text-[13px] font-light leading-[1.7] text-bone/80">
                  <p>{tr("São Paulo, Brasil")}</p>
                  <p>{tr("Works shown and developed worldwide")}</p>
                </div>
              </div>
            </div>
          </aside>
        </section>
      </main>

      <section aria-label={tr("Luiza Jesus do Prado — film")} className="w-full">
        <VideoEmbed
          title={tr("Luiza Jesus do Prado — film")}
          youtube="6kJ6c-HkE4k"
          poster={contactVideoPoster.url}
        />
      </section>

      <SiteFooter />
    </div>
  );
}
